import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import { HeaderSwitchWrapper } from '@components/Header/components/HeaderSwitchWrapper';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { downloadCSV } from '@helpers/downloadCSV';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { parseQueryParamInt } from '@helpers/parseQueryParamInt';
import { useAPI } from '@providers/useAPI';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import PencilSvg from '@components/icons/Pencil.svg';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { attorneysKeys, TAttorneysTransactionsListParams } from 'app/queries/attorneys/attorneysKeys';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { fetchAllAttorneysTransactionsForExport } from './api/fetchAllAttorneysTransactionsForExport';
import { createFetchAttorneysTransactionsQueryFn } from './api/fetchAttorneysTransactions';
import { AttorneysTable } from './components/AttorneysTable';
import { ATTORNEYS_REPORT_CSV_HEADERS, buildAttorneysReportCsvRows } from './helpers/buildAttorneysReportCsvRows';
import { getAttorneysReportPeriod } from './helpers/getAttorneysReportPeriod';
import { MainContent } from './styles/MainContent';

const SEARCH_DEBOUNCE_MS = 300;

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const AttorneysReportPage = () => {
  const navigate = useNavigate();
  const { call } = useAPI();
  const { openModal } = useModalContext();
  const { organization } = useAuthContext();
  const organizationId = organization?.id ?? '';
  const {
    searchParams,
    page,
    pageSize,
    search,
    orderBy,
    orderType,
    setSearch,
    setPage,
    setPageSize,
    toggleSort,
    updateParams,
  } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });

  const currentMonth = new Date().getDate() >= 15 ? new Date().getMonth() + 1 : new Date().getMonth();
  const month = parseQueryParamInt(searchParams.get('month'), {
    fallback: currentMonth > 11 ? 0 : currentMonth,
    min: 0,
    max: 11,
  });
  const year = parseQueryParamInt(searchParams.get('year'), {
    fallback: new Date().getFullYear() + (currentMonth > 11 ? 1 : 0),
    min: 1970,
    max: 3000,
  });
  const applyPeriod = (nextMonth: number, nextYear: number) =>
    updateParams({ month: String(nextMonth), year: String(nextYear) }, { resetPage: true });

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TAttorneysTransactionsListParams>(
    () => ({ page, pageSize, search: debouncedSearch, orderBy, orderType, ...getAttorneysReportPeriod(month, year) }),
    [debouncedSearch, month, orderBy, orderType, page, pageSize, year],
  );

  const transactionsQuery = useQuery({
    queryKey: attorneysKeys.transactionList(organizationId, listParams),
    queryFn: createFetchAttorneysTransactionsQueryFn(call),
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey[0];
      const isSamePeriod =
        previousKey?.organizationId === organizationId &&
        previousKey.params.from === listParams.from &&
        previousKey.params.to === listParams.to;
      return isSamePeriod ? previousData : undefined;
    },
    enabled: organizationId !== '' && !isSearchSettling,
  });
  const attorneysTransactions = transactionsQuery.data;
  const transactionsCount = attorneysTransactions?.count ?? 0;
  const hasTransactions = !!attorneysTransactions?.data.length;

  const exportMutation = useMutation({
    mutationFn: () => fetchAllAttorneysTransactionsForExport(call, organizationId, listParams),
    onSuccess: (response) => {
      downloadCSV(buildAttorneysReportCsvRows(response.data), ATTORNEYS_REPORT_CSV_HEADERS);
    },
    onError: () => toast.error('Failed to export the attorneys report'),
  });

  const shouldShowReports = !!organization?.permissions?.viewReports;

  const openMonthModal = () => {
    openModal(ModalNamesEnum.PeriodPicker, {
      month,
      year,
      onApply: applyPeriod,
    });
  };

  const isLoading = transactionsQuery.isFetching || isSearchSettling || exportMutation.isPending;
  const isExportDisabled =
    isSearchSettling || transactionsQuery.isPlaceholderData || !transactionsQuery.isSuccess || exportMutation.isPending;

  return (
    <>
      <Helmet>
        <title>Attorneys Report - AutoScript</title>
      </Helmet>
      <Header
        leftSideContent={[
          <HeaderSwitchWrapper key="tabSwitcher">
            <SwitchButton
              onClick={() => {
                navigate(routes.organizationMembers.make(organization?.slug ?? ''));
              }}
            >
              Members
            </SwitchButton>
            <SwitchButton
              onClick={() => {
                navigate(routes.invitations.make(organization?.slug ?? ''));
              }}
            >
              Invitations
            </SwitchButton>
            {shouldShowReports ? (
              <SwitchButton
                onClick={() => {
                  navigate(routes.organizationMemberReports.make(organization?.slug ?? ''));
                }}
              >
                Reports
              </SwitchButton>
            ) : null}
            {shouldShowReports && organization?.featureFlags.ATTORNEY_VIEW_AVAILABLE ? (
              <SwitchButton
                active
                onClick={() => {
                  navigate(routes.organizationAttorneysReport.make(organization?.slug ?? ''));
                }}
              >
                Attorneys Report
              </SwitchButton>
            ) : null}
          </HeaderSwitchWrapper>,
        ]}
        title="Attorneys Report"
        buttonLabel={hasTransactions ? 'Export CSV' : undefined}
        buttonOnClick={hasTransactions ? () => exportMutation.mutate() : undefined}
        buttonDisabled={isExportDisabled}
        buttonLoading={exportMutation.isPending}
        setSearch={setSearch}
        search={search}
      />

      <LinearProgressLoader active={isLoading} />
      <MainContent>
        {transactionsQuery.isError ? (
          <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
            <span>Failed to load the attorneys report.</span>
            <TextButton type="button" onClick={() => void transactionsQuery.refetch()}>
              Retry
            </TextButton>
          </div>
        ) : null}
        {attorneysTransactions?.stats ? (
          <div className="head">
            <div className="month">
              {months[month]} {year}
              <IconButtonClean
                type="button"
                title="Change month"
                className="ml-[10px]"
                onClick={() => openMonthModal()}
              >
                <PencilSvg />
              </IconButtonClean>
            </div>
            <div className="right-side">
              <div className="line">
                Total Attorney <span>Invitations</span>: {attorneysTransactions.stats.invites}
              </div>
              <div className="line">
                Total Attorney <span>Connections</span>: {attorneysTransactions.stats.connects}
              </div>
              <div className="line">
                <span>Total costs</span>: <span>{formatCentsPrice(attorneysTransactions.stats.amount)}</span>
              </div>
            </div>
          </div>
        ) : null}
        <AttorneysTable
          attorneysTransactions={attorneysTransactions?.data ?? []}
          sortingField={orderBy}
          sortingReverse={orderType === 'desc'}
          toggleSort={toggleSort}
        />
        <PagePagination
          totalItemsCount={transactionsCount}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </MainContent>
    </>
  );
};
